import React from 'react';
import {
	Deal,
	DealExcludedLender,
	Guid,
	PartialBank,
	Tier1BankTypeMap,
	Tier1BankTypes,
	getExlcudedLendersBase,
} from '../../shared/models';
import {
	CerebroCheckbox,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../shared/components';
import { Field, Formik, FormikErrors } from 'formik';
import { Grid } from '@mui/material';
import { FormikUtils } from '../../shared/utils';
import { LendersAutoComplete } from './LendersAutoComplete';
import { useMutation, useQueryClient } from 'react-query';
import { useApi, useAuth } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { QueryKeys } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { AxiosResponse } from 'axios';
import { getApiResponseData } from '../../shared/utils/axios';

interface ExcludedLenderDrawerProps {
	open: boolean;
	onClose: () => void;
	deal: Deal | undefined;
	lenders: DealExcludedLender[] | undefined;
}

export const ExcludedLenderDrawer: React.FC<ExcludedLenderDrawerProps> = ({ open, onClose, deal, lenders }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const auth = useAuth();

	const ref = React.useRef<HTMLDivElement>(null);
	const [leaveDrawerOpen, setLeaveDrawerOpen] = React.useState<boolean>(false);
	const canAddAnother = !lenders || lenders.length < deal!.excludedLenderCount - 1;

	const lenderMutation = useMutation(
		async ({ values }: ExcludeLenderMutationVariables) => {
			const createDto: CreateDealExcludedLenderDto = {
				isIncumbentLender: values.isIncumbent ?? false,
				tier1BankType: values.tier1BankType ?? null,
			};

			if (values.newLender) createDto.bankName = values.newLenderName;
			else createDto.bankGuid = values.lender?.guid;

			let updatedDeal: Deal | null = null;

			// If they are excluding a lender but haven't saved the toggle for excluding lenders, save the toggle to true.
			if (deal?.hasExcludedLenders !== true) {
				const { data, error } = await getApiResponseData<Deal>(
					api.patch(CerebroApiEndpoints.Deals.Root(deal?.guid), {
						hasExcludedLenders: true,
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				updatedDeal = data;
			}

			const { data, error } = await getApiResponseData<DealExcludedLender>(
				api.post<CreateDealExcludedLenderDto, AxiosResponse<DealExcludedLender>>(
					CerebroApiEndpoints.ExcludedLenders.Root(deal?.guid),
					createDto
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return { updatedDeal, data };
		},
		{
			onSuccess: ({ updatedDeal, data }) => {
				if (updatedDeal !== null) client.setQueryData(QueryKeys.Deal.Root(deal?.guid), updatedDeal);

				const closeDrawer = !auth.currentUser.isEmployeeUser && lenders && lenders.length === 4;

				client.invalidateQueries(QueryKeys.ExcludedLenders(deal?.guid));

				snackbar.enqueueSnackbar(`${data?.bankName} has now been excluded.`, {
					variant: 'success',
				});

				if (closeDrawer) onClose();
			},
			onError: (error: Error) => {
				client.invalidateQueries(QueryKeys.ReducedFeeLenders(deal?.guid));
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
				if (leaveDrawerOpen) {
					helpers.resetForm({
						values: {
							lender: null,
							newLender: false,
							newLenderName: null,
							tier1BankType: null,
							isIncumbent: null,
						} as AddExcludedLenderFormValues,
					});
				} else {
					onClose();
				}
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading sx={{ marginBottom: 3 }}>Exclude Lender</CerebroHeading>
			<Formik
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(lenderMutation.mutate)}
				initialValues={
					{
						lender: null,
						newLender: false,
						newLenderName: null,
						tier1BankType: null,
						isIncumbent: null,
					} as AddExcludedLenderFormValues
				}
			>
				{({ isSubmitting, values, touched, errors, handleSubmit }) => (
					<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ paddingRight: 3, minWidth: { md: 500 } }}>
						<CerebroLabel htmlFor="lender" sx={{ marginTop: 3 }}>
							Select a lender to exclude:
						</CerebroLabel>
						<Field
							id="lender"
							name="lender"
							lenders={getExlcudedLendersBase(lenders)}
							component={LendersAutoComplete}
							companyGuid={deal?.companyGuid}
							disabled={values.newLender}
							error={FormikUtils.IsInvalid('lender', errors, touched)}
						/>

						<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
							<Grid item>
								{FormikUtils.IsInvalid('lender', errors, touched) && (
									<CerebroText variant="error">{errors.lender}</CerebroText>
								)}
							</Grid>
							<Grid item>
								<CerebroLabel htmlFor="newLender" sx={{ display: 'flex', alignItems: 'center' }}>
									<Field
										id="newLender"
										name="newLender"
										size="small"
										as={CerebroCheckbox}
										sx={{ padding: 0, paddingRight: 1 }}
									/>
									<CerebroText variant="hint" component="span">
										Include a lender not listed
									</CerebroText>
								</CerebroLabel>
							</Grid>
						</Grid>

						{values.newLender && (
							<>
								<CerebroLabel htmlFor="newLenderName" sx={{ marginTop: 3 }}>
									New Lender:
								</CerebroLabel>
								<Field
									id="newLenderName"
									name="newLenderName"
									as={CerebroInput}
									placeholder="The full name of the lender to exclude"
									error={FormikUtils.IsInvalid('newLenderName', errors, touched)}
								/>
								{FormikUtils.IsInvalid('newLenderName', errors, touched) && (
									<CerebroText variant="error">{errors.newLenderName}</CerebroText>
								)}

								<CerebroLabel id="tier1BankType" sx={{ marginTop: 3 }}>
									Lender Type
								</CerebroLabel>
								<Field
									id="tier1BankType"
									as={CerebroDropdown}
									name="tier1BankType"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('tier1BankType', errors, touched)}
								>
									{Object.entries(Tier1BankTypeMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
								{FormikUtils.IsInvalid('tier1BankType', errors, touched) && (
									<CerebroText variant="error">{errors.tier1BankType}</CerebroText>
								)}
							</>
						)}

						<CerebroLabel htmlFor="isIncumbent" sx={{ marginTop: 3 }}>
							Is this an incumbent lender?
						</CerebroLabel>
						<Field
							small
							id="isIncumbent"
							name="isIncumbent"
							as={CerebroYesNo}
							error={FormikUtils.IsInvalid('isIncumbent', errors, touched)}
						/>
						{FormikUtils.IsInvalid('isIncumbent', errors, touched) && (
							<CerebroText variant="error">{errors.isIncumbent}</CerebroText>
						)}

						<Grid container spacing={3}>
							<Grid item xs={6}>
								<CerebroPrimaryButton
									loading={isSubmitting}
									type="submit"
									onClick={() => setLeaveDrawerOpen(false)}
									sx={{ marginTop: 4 }}
								>
									Save &#38; Continue
								</CerebroPrimaryButton>
							</Grid>

							{canAddAnother && (
								<Grid item xs={6}>
									<CerebroPrimaryButton
										loading={isSubmitting}
										type="submit"
										onClick={() => setLeaveDrawerOpen(true)}
										sx={{ marginTop: 4 }}
									>
										Save &#38; Add Another
									</CerebroPrimaryButton>
								</Grid>
							)}
						</Grid>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

function validateForm(values: AddExcludedLenderFormValues) {
	const errors: AddExcludedLenderFormValueErrors = {};

	if (values.newLender) {
		if (!values.newLenderName) {
			errors.newLenderName = 'You must enter a valid bank name.';
		}

		if (!values.tier1BankType) {
			errors.tier1BankType = 'You must select a type to add a lender.';
		}
	} else {
		if (!values.lender || !values.lender.name) {
			errors.lender = 'You must select a lender associated to the terms.';
		}
	}

	if (values.isIncumbent == null) {
		errors.isIncumbent = 'Please indicate whether the lender is incumbent.';
	}

	return errors;
}

interface AddExcludedLenderFormValues {
	lender: PartialBank | null;
	newLender: boolean;
	newLenderName: string | null;
	tier1BankType: Tier1BankTypes | null;
	isIncumbent: boolean | null;
}

type AddExcludedLenderFormValueErrors = FormikErrors<AddExcludedLenderFormValues>;
type ExcludeLenderMutationVariables = FormikUtils.MutateOnSubmitVariables<AddExcludedLenderFormValues>;

interface CreateDealExcludedLenderDto {
	bankGuid?: Guid | null;
	bankName?: string | null;
	tier1BankType: Tier1BankTypes | null;
	isIncumbentLender: boolean;
}
